import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Gift, Trophy, TrendingUp, ChevronRight, Sparkles, Target } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import apiClient, {
  LoyaltyOverview,
  TierProgress,
  BadgesResponse,
  ChallengesResponse,
} from '@/lib/api-client';

export const Points: React.FC = () => {
  const { customer, isLoading: authLoading, error: authError } = useAuth();
  const [loyalty, setLoyalty] = useState<LoyaltyOverview | null>(null);
  const [tierProgress, setTierProgress] = useState<TierProgress | null>(null);
  const [badges, setBadges] = useState<BadgesResponse | null>(null);
  const [challenges, setChallenges] = useState<ChallengesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [convertAmount, setConvertAmount] = useState<number>(100);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    const fetchLoyaltyData = async () => {
      console.log('[Points] fetchLoyaltyData called, customer:', customer ? 'present' : 'null');

      // Wait for customer to be set (authentication complete)
      if (!customer) {
        console.log('[Points] No customer yet, waiting...');
        return;
      }

      // Get token from apiClient or try localStorage
      const token = apiClient.getToken();
      console.log('[Points] apiClient token:', token ? token.substring(0, 10) + '...' : 'null');
      console.log('[Points] apiClient.isAuthenticated():', apiClient.isAuthenticated());

      // If apiClient doesn't have token, try to get from localStorage
      if (!token) {
        const storedToken = localStorage.getItem('customer_token');
        console.log('[Points] localStorage token:', storedToken ? storedToken.substring(0, 10) + '...' : 'null');
        if (storedToken) {
          apiClient.setToken(storedToken);
        } else {
          console.log('[Points] No token available, cannot fetch data');
          setIsLoading(false);
          return;
        }
      }

      try {
        setIsLoading(true);
        console.log('[Points] Fetching loyalty data...');
        const [loyaltyData, tierData, badgesData, challengesData] = await Promise.all([
          apiClient.getLoyaltyOverview(),
          apiClient.getTierProgress(),
          apiClient.getBadges(),
          apiClient.getChallenges(),
        ]);
        console.log('[Points] Loyalty data received:', loyaltyData);

        setLoyalty(loyaltyData);
        setTierProgress(tierData);
        setBadges(badgesData);
        setChallenges(challengesData);
      } catch (err) {
        console.error('[Points] Error fetching loyalty data:', err);
        setError('Failed to load loyalty data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLoyaltyData();
  }, [customer]); // Re-run when customer changes (authentication completes)

  const handleConvertPoints = async () => {
    if (!loyalty || convertAmount <= 0 || convertAmount > loyalty.currentBalance) return;

    try {
      setIsConverting(true);
      const result = await apiClient.convertPointsToCredit(convertAmount);
      if (result.success) {
        setLoyalty(prev => prev ? {
          ...prev,
          currentBalance: result.newPointsBalance,
          totalRedeemed: prev.totalRedeemed + result.pointsConverted,
        } : null);
      }
    } catch (err) {
      console.error('Error converting points:', err);
    } finally {
      setIsConverting(false);
    }
  };

  const handleJoinChallenge = async (challengeId: string) => {
    try {
      await apiClient.joinChallenge(challengeId);
      // Refresh challenges
      const challengesData = await apiClient.getChallenges();
      setChallenges(challengesData);
    } catch (err) {
      console.error('Error joining challenge:', err);
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading your points...</p>
        </div>
      </div>
    );
  }

  if (authError || error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card elevation="md" className="max-w-md">
          <CardBody>
            <div className="text-center">
              <p className="text-error-600 font-medium mb-4">{authError || error}</p>
              <p className="text-neutral-600 text-sm">
                Please request a new login link to access your points.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card elevation="md" className="max-w-md">
          <CardBody>
            <div className="text-center">
              <p className="text-neutral-900 font-medium mb-2">Welcome!</p>
              <p className="text-neutral-600 text-sm">
                Please check your email for your magic link to access your dashboard.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!loyalty?.enrolled) {
    return (
      <div className="space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-warm rounded-2xl p-6 md:p-8 text-center"
        >
          <Sparkles className="w-16 h-16 mx-auto text-primary-600 mb-4" />
          <h1 className="text-3xl font-display font-bold text-neutral-900 mb-2">
            Join Our Rewards Program
          </h1>
          <p className="text-neutral-700 text-lg mb-6">
            Earn points on every purchase, get exclusive rewards, and unlock special perks!
          </p>
          <Button variant="primary" gradient size="lg">
            Get Started
          </Button>
        </motion.section>
      </div>
    );
  }

  return (
    <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-6"
      >
        {/* Points Balance Hero */}
        <motion.section
          variants={item}
          className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 md:p-8 text-white"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-primary-100 text-sm mb-1">Your Points Balance</p>
              <h1 className="text-5xl md:text-6xl font-display font-bold mb-2">
                {loyalty.currentBalance.toLocaleString()}
              </h1>
              <p className="text-primary-200">
                {loyalty.pointsToNextTier ? `${loyalty.pointsToNextTier.toLocaleString()} points to next tier` : 'Top tier reached!'}
              </p>
            </div>
            <div className="bg-white/20 rounded-full p-4">
              <Star className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{loyalty.totalEarned.toLocaleString()}</p>
              <p className="text-xs text-primary-200">Total Earned</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{loyalty.totalRedeemed.toLocaleString()}</p>
              <p className="text-xs text-primary-200">Redeemed</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{loyalty.referralCount}</p>
              <p className="text-xs text-primary-200">Referrals</p>
            </div>
          </div>
        </motion.section>

        {/* Tier Progress */}
        {tierProgress?.enrolled && (
          <motion.div variants={item}>
            <Card elevation="md" gradient gradientDirection="cool">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-warning-500" />
                  <CardTitle>{tierProgress.currentTier} Member</CardTitle>
                </div>
                <CardDescription>
                  {tierProgress.currentMultiplier}x points multiplier
                </CardDescription>
              </CardHeader>
              <CardBody>
                {/* Tier Progress Bar */}
                {tierProgress.nextTier && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-neutral-600">{tierProgress.currentTier}</span>
                      <span className="text-neutral-600">{tierProgress.nextTier.name}</span>
                    </div>
                    <div className="h-3 bg-neutral-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-warning-400 to-warning-500 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(100, ((tierProgress.totalPointsEarned) / tierProgress.nextTier.minPoints) * 100)}%`,
                        }}
                      />
                    </div>
                    <p className="text-sm text-neutral-600 mt-2">
                      {tierProgress.nextTier.pointsNeeded.toLocaleString()} points needed for {tierProgress.nextTier.name}
                    </p>
                  </div>
                )}

                {/* Current Benefits */}
                <div className="space-y-2">
                  <p className="font-medium text-neutral-800">Your Benefits:</p>
                  {tierProgress.currentBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-neutral-600">
                      <ChevronRight className="w-4 h-4 text-success-500" />
                      {benefit}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Convert Points to Credit */}
        <motion.div variants={item}>
          <Card elevation="md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Gift className="w-6 h-6 text-accent-600" />
                <CardTitle>Convert Points to Store Credit</CardTitle>
              </div>
              <CardDescription>
                100 points = $1.00 store credit
              </CardDescription>
            </CardHeader>
            <CardBody>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm text-neutral-600 mb-2">Points to convert</label>
                  <input
                    type="number"
                    min="100"
                    step="100"
                    max={loyalty.currentBalance}
                    value={convertAmount}
                    onChange={(e) => setConvertAmount(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="text-center pt-6">
                  <ChevronRight className="w-6 h-6 text-neutral-400" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-neutral-600 mb-2">Store credit</label>
                  <div className="px-4 py-2 bg-success-50 border border-success-200 rounded-lg text-success-700 font-bold text-lg">
                    ${(convertAmount / 100).toFixed(2)}
                  </div>
                </div>
              </div>
              <Button
                variant="primary"
                gradient
                fullWidth
                className="mt-4"
                onClick={handleConvertPoints}
                disabled={isConverting || convertAmount > loyalty.currentBalance || convertAmount < 100}
              >
                {isConverting ? 'Converting...' : 'Convert Points'}
              </Button>
            </CardBody>
          </Card>
        </motion.div>

        {/* Badges */}
        {badges && badges.totalAvailable > 0 && (
          <motion.div variants={item}>
            <Card elevation="md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-accent-500" />
                    <CardTitle>Your Badges</CardTitle>
                  </div>
                  <span className="text-sm text-neutral-500">
                    {badges.totalEarned} / {badges.totalAvailable} earned
                  </span>
                </div>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                  {badges.available.map((badge) => (
                    <div
                      key={badge.id}
                      className={`relative p-3 rounded-xl text-center transition-all ${
                        badge.earned
                          ? 'bg-gradient-to-br from-warning-50 to-warning-100 border-2 border-warning-300'
                          : 'bg-neutral-100 opacity-50'
                      }`}
                    >
                      <div className="text-3xl mb-1">
                        {badge.iconEmoji || (badge.earned ? 'icon' : '?')}
                      </div>
                      <p className="text-xs font-medium text-neutral-700 truncate">
                        {badge.name}
                      </p>
                      {badge.earned && (
                        <div className="absolute -top-1 -right-1 bg-success-500 rounded-full p-1">
                          <Star className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Active Challenges */}
        {challenges && challenges.activeCount > 0 && (
          <motion.div variants={item}>
            <Card elevation="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Target className="w-6 h-6 text-primary-600" />
                  <CardTitle>Active Challenges</CardTitle>
                </div>
                <CardDescription>
                  {challenges.completedCount} completed, {challenges.joinedCount} in progress
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  {challenges.challenges.map((item) => (
                    <div
                      key={item.challenge.id}
                      className={`p-4 rounded-xl border-2 ${
                        item.isCompleted
                          ? 'bg-success-50 border-success-200'
                          : item.isJoined
                          ? 'bg-primary-50 border-primary-200'
                          : 'bg-neutral-50 border-neutral-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-neutral-900">{item.challenge.name}</h4>
                          {item.challenge.description && (
                            <p className="text-sm text-neutral-600 mt-1">{item.challenge.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-sm text-primary-600 font-medium">
                              +{item.challenge.pointsReward} points
                            </span>
                            <span className="text-xs text-neutral-500">
                              Ends {new Date(item.challenge.endDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        {item.isCompleted ? (
                          <div className="bg-success-500 rounded-full p-2">
                            <Star className="w-5 h-5 text-white" />
                          </div>
                        ) : item.isJoined ? (
                          <div className="text-right">
                            <div className="text-2xl font-bold text-primary-600">
                              {Math.round(item.progressPercent)}%
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleJoinChallenge(item.challenge.id)}
                          >
                            Join
                          </Button>
                        )}
                      </div>
                      {item.isJoined && !item.isCompleted && (
                        <div className="mt-3">
                          <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-500 rounded-full transition-all duration-500"
                              style={{ width: `${item.progressPercent}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Recent Transactions */}
        {loyalty.recentTransactions.length > 0 && (
          <motion.div variants={item}>
            <Card elevation="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-neutral-600" />
                  <CardTitle>Recent Activity</CardTitle>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {loyalty.recentTransactions.map((tx, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-neutral-800">{tx.reason}</p>
                        <p className="text-xs text-neutral-500">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span
                        className={`font-bold ${
                          tx.type === 'Earned' ? 'text-success-600' : 'text-neutral-600'
                        }`}
                      >
                        {tx.type === 'Earned' ? '+' : '-'}{tx.points.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </motion.div>
  );
};

export default Points;
